import type { Station, Prediction, RailIncident } from './api';
import { getStationLineCodes, getPredictionsForStation } from './api';
import { LINE_COLORS } from './data/stations-meta';
import { flyToStation, switchMapStyle, isDarkMode, clearActiveStation, flyToUserLocation } from './map';

// ── DOM References ──

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

const alertBanner = $('alert-banner');
const alertText = $('alert-text');
const alertDismiss = $('alert-dismiss');
const searchInput = $<HTMLInputElement>('search-input');
const searchResults = $('search-results');
const stationPanel = $('station-panel');
const stationNameEl = $('station-name');
const stationLinesEl = $('station-lines');
const stationAddressEl = $('station-address');
const predictionsLoading = $('predictions-loading');
const predictionsEmpty = $('predictions-empty');
const predictionsTable = $('predictions-table');
const predictionsBody = $('predictions-body');
const stationClose = $('station-close');
const themeToggle = $('theme-toggle');
const locateBtn = $('locate-btn');

let allStations: Station[] = [];
let allPredictions: Prediction[] = [];
let predictionsLoaded = false;
let currentStationCode: string | null = null;

// ── Search ──

export function initSearch(stations: Station[]) {
  allStations = stations;

  searchInput.addEventListener('input', () => {
    const query = searchInput.value.trim().toLowerCase();
    if (query.length === 0) {
      searchResults.hidden = true;
      return;
    }

    const matches = allStations
      .filter((s) => s.Name.toLowerCase().includes(query))
      .slice(0, 10);

    if (matches.length === 0) {
      searchResults.hidden = true;
      return;
    }

    searchResults.innerHTML = matches
      .map(
        (s) => `
      <div class="search-result-item" data-code="${s.Code}">
        <div class="search-result-lines">${getStationLineCodes(s).map((lc) => lineBadgeHtml(lc)).join('')}</div>
        <span class="search-result-name">${highlightMatch(s.Name, query)}</span>
      </div>
    `
      )
      .join('');

    searchResults.hidden = false;
  });

  searchResults.addEventListener('click', (e) => {
    const item = (e.target as HTMLElement).closest('.search-result-item') as HTMLElement;
    if (!item) return;
    const code = item.dataset.code!;
    const station = allStations.find((s) => s.Code === code);
    if (station) {
      searchInput.value = '';
      searchResults.hidden = true;
      flyToStation(code);
      showStationPanel(station);
    }
  });

  // Close search on outside click
  document.addEventListener('click', (e) => {
    if (!(e.target as HTMLElement).closest('.search-container')) {
      searchResults.hidden = true;
    }
  });

  // Keyboard navigation
  searchInput.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      searchInput.value = '';
      searchResults.hidden = true;
      searchInput.blur();
    }
    if (e.key === 'Enter') {
      const first = searchResults.querySelector('.search-result-item') as HTMLElement;
      first?.click();
    }
  });
}

function highlightMatch(text: string, query: string): string {
  const idx = text.toLowerCase().indexOf(query);
  if (idx === -1) return text;
  return (
    text.slice(0, idx) +
    `<strong>${text.slice(idx, idx + query.length)}</strong>` +
    text.slice(idx + query.length)
  );
}

// ── Station Panel ──

export function showStationPanel(station: Station) {
  currentStationCode = station.Code;

  stationNameEl.textContent = station.Name;
  stationLinesEl.innerHTML = getStationLineCodes(station)
    .map((lc) => lineBadgeHtml(lc, true))
    .join('');
  stationAddressEl.textContent = station.Address
    ? `${station.Address.Street}, ${station.Address.City}, ${station.Address.State} ${station.Address.Zip}`
    : '';

  stationPanel.hidden = false;

  // If predictions have been fetched at least once, render immediately
  if (predictionsLoaded) {
    renderPredictions(station.Code);
  } else {
    predictionsLoading.style.display = 'flex';
    (predictionsEmpty as HTMLElement).style.display = 'none';
    (predictionsTable as HTMLElement).style.display = 'none';
  }
}

export function hideStationPanel() {
  stationPanel.hidden = true;
  currentStationCode = null;
  clearActiveStation();
}

export function updatePredictions(predictions: Prediction[]) {
  allPredictions = predictions;
  predictionsLoaded = true;
  if (currentStationCode) {
    renderPredictions(currentStationCode);
  }
}

function renderPredictions(stationCode: string) {
  const station = allStations.find((s) => s.Code === stationCode);
  if (!station) return;

  // Include all platforms at this station (same name = same physical location)
  const codes = [
    ...new Set([
      stationCode,
      station.StationTogether1,
      station.StationTogether2,
      ...allStations.filter((s) => s.Name === station.Name).map((s) => s.Code),
    ].filter(Boolean)),
  ];

  let preds = codes.flatMap((code) =>
    getPredictionsForStation(allPredictions, code)
  );

  // Fallback: match by LocationName in case codes don't align
  if (preds.length === 0 && station.Name) {
    preds = allPredictions
      .filter((p) => (p.LocationName || '').toLowerCase() === station.Name.toLowerCase())
      .sort((a, b) => {
        const aMin = a.Min === 'ARR' ? -1 : a.Min === 'BRD' ? -2 : parseInt(a.Min) || 999;
        const bMin = b.Min === 'ARR' ? -1 : b.Min === 'BRD' ? -2 : parseInt(b.Min) || 999;
        return aMin - bMin;
      });
  }

  predictionsLoading.style.display = 'none';

  if (preds.length === 0) {
    (predictionsEmpty as HTMLElement).style.display = 'flex';
    (predictionsTable as HTMLElement).style.display = 'none';
    return;
  }

  (predictionsEmpty as HTMLElement).style.display = 'none';
  (predictionsTable as HTMLElement).style.display = 'table';

  predictionsBody.innerHTML = preds
    .map((p) => {
      const minClass =
        p.Min === 'ARR' ? 'arriving' : p.Min === 'BRD' ? 'boarding' : '';
      const minDisplay =
        p.Min === 'ARR' ? 'ARR' : p.Min === 'BRD' ? 'BRD' : p.Min === '' ? '—' : `${p.Min}`;
      const carsDisplay = p.Car ? p.Car : '—';
      const lineCode = p.Line || '';

      return `
        <tr>
          <td>${lineCode ? lineBadgeHtml(lineCode) : '<span style="color:var(--text-tertiary)">—</span>'}</td>
          <td class="prediction-destination">${p.DestinationName || p.Destination || '—'}</td>
          <td><span class="prediction-min ${minClass}">${minDisplay}</span></td>
          <td><span class="prediction-cars">${carsDisplay}</span></td>
        </tr>
      `;
    })
    .join('');
}

// ── Alert Banner ──

export function updateAlerts(incidents: RailIncident[]) {
  const count = incidents.length;
  if (count === 0) {
    alertBanner.hidden = true;
    return;
  }

  const descriptions = incidents
    .map((i) => i.Description.replace(/<[^>]*>/g, '').trim())
    .filter(Boolean);

  if (descriptions.length === 0) {
    alertBanner.hidden = true;
    return;
  }

  alertText.textContent =
    descriptions.length === 1
      ? descriptions[0]
      : `${descriptions[0]} (+${descriptions.length - 1} more)`;

  alertBanner.hidden = false;
}

// ── Theme Toggle ──

export function initThemeToggle() {
  // Load saved preference
  const saved = localStorage.getItem('wmata-theme');
  if (saved) {
    document.documentElement.setAttribute('data-theme', saved);
  }

  themeToggle.addEventListener('click', () => {
    const currentlyDark = isDarkMode();
    const newTheme = currentlyDark ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('wmata-theme', newTheme);
    switchMapStyle(!currentlyDark);
  });
}

// ── Location ──

export function initLocate() {
  if (!locateBtn) return;
  locateBtn.addEventListener('click', () => {
    locateBtn.classList.add('loading');
    flyToUserLocation(
      (nearest) => {
        locateBtn.classList.remove('loading');
        if (nearest) showStationPanel(nearest);
      },
      (err) => {
        locateBtn.classList.remove('loading');
        console.warn('Location error:', err.message);
      }
    );
  });
}

// ── Event Listeners ──

export function initUIEvents() {
  stationClose.addEventListener('click', hideStationPanel);

  alertDismiss.addEventListener('click', () => {
    alertBanner.hidden = true;
  });

  // Global keyboard shortcut: Cmd/Ctrl + K to focus search
  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      searchInput.focus();
      searchInput.select();
    }
    if (e.key === 'Escape' && !stationPanel.hidden) {
      hideStationPanel();
    }
  });
}

// ── Helpers ──

function lineBadgeHtml(lineCode: string, large = false): string {
  const sizeClass = large ? ' large' : '';
  return `<span class="line-badge${sizeClass}" data-line="${lineCode}" style="background:${LINE_COLORS[lineCode] || '#888'}${lineCode === 'YL' ? ';color:#1d1d1f' : ''}">${lineCode}</span>`;
}
